// Maxin that deals with app navigation
// IMPORTANT: This alters the default behavior of Vue router. Always use the exposed methods from this helper to manage app navigation

import changeCase from 'change-case';

import { i18n } from '@/i18n';
import AboutUs from '@/views/AboutUs.vue';
import AutoDownloadSettings from '@/views/AutoDownloadSettings.vue';
import ChangePassword from '@/views/ChangePassword.vue';
import Channel from '@/views/Channel.vue';
import Episode from '@/views/Episode.vue';
import ForgetPassword from '@/views/ForgetPassword.vue';
import Invitation from '@/views/Invitation.vue';
import Host from '@/views/Host.vue';
import Login from '@/views/Login.vue';
import MainTabbar from '@/views/MainTabbar.vue';
import NotificationSettings from '@/views/NotificationSettings.vue';
import PrivacySettings from '@/views/PrivacySettings.vue';
import ProfileEdit from '@/views/ProfileEdit.vue';
import ResetPassword from '@/views/ResetPassword.vue';
import Search from '@/views/Search.vue';
import Signup from '@/views/Signup.vue';
import SkipRewindSettings from '@/views/SkipRewindSettings.vue';
import SleepTimerSettings from '@/views/SleepTimerSettings.vue';
import Splash from '@/views/Splash.vue';
import Tag from '@/views/Tag.vue';

const PAGES = {
  AboutUs: AboutUs,
  AutoDownloadSettings: AutoDownloadSettings,
  ChangePassword: ChangePassword,
  Channel: Channel,
  Episode: Episode,
  ForgetPassword: ForgetPassword,
  Invitation: Invitation,
  Host: Host,
  Login: Login,
  MainTabbar: MainTabbar,
  NotificationSettings: NotificationSettings,
  PrivacySettings: PrivacySettings,
  ProfileEdit: ProfileEdit,
  ResetPassword: ResetPassword,
  Search: Search,
  Signup: Signup,
  SkipRewindSettings: SkipRewindSettings,
  SleepTimerSettings: SleepTimerSettings,
  Splash: Splash,
  Tag: Tag,
};

let notificationClickListenerIsOn = false;

export default {
  data() {
    return {
      shouldShowGoOnlineToast: false,
    };
  },

  methods: {
    // Navigate to (redirect to) the first page based on the given url after the splash/load screen
    // should only be called once at app start
    navigatorInitRoot(url) {
      let pageParams = this._getPageParamsByRouter(url) || {};
      this.$store.dispatch('currentUser/load')
        .then((result) => {
          if (result) {
            if (result._is_guest && pageParams.initFrom === 'login') {
              this.navigatorReset('Login', { pageParamsAfterLoad: pageParams }, { animation: 'none' });
            } else {
              this.navigatorReset('MainTabbar', { pageParamsAfterLoad: pageParams }, { animation: 'none' });
            }
            if (!result._is_guest && pageParams.initFrom === 'login') {
              this.$ons.notification.toast(i18n.t("messages.session_exist"), { timeout: 3000, animation: 'fall' });
            }
            this.setupPushNotification();
          } else {
            this.navigatorReset('Login', { pageParamsAfterLoad: pageParams }, { animation: 'none' });
            console.log('Not logged in');
            if (pageParams.initFrom === 'main-tabbar') {
              this.$ons.notification.toast(i18n.t("messages.login_required"), { timeout: 3000, animation: 'fall' });
            }
          }
        }).catch((err) => {
          console.log(err);
          this.navigatorReset('Login', { pageParamsAfterLoad: pageParams }, { animation: 'none' });
          if (!!window.cordova && (!url || pageParams.routeName === 'login' || pageParams.initFrom === 'main-tabbar')) {
            this.showSomethingWrongToast();
            return;
          }
          if (pageParams.initFrom === 'main-tabbar') {
            this.$ons.notification.toast(i18n.t("messages.login_required"), { timeout: 3000, animation: 'fall' });
          }
        });
    },
    _getPageParamsByRouter(url) {
      let route = null;
      if (url) {
        let parsedUrl = new URL(url);
        route = this.$router.resolve(parsedUrl.pathname + parsedUrl.search + parsedUrl.hash).route;
      } else {
        route = this.$router.currentRoute;
      }
      if (!route.name) { return null; }
      let pageName = changeCase.pascalCase(route.name);
      if (['Home', 'Subscriptions', 'Downloads', 'Account', 'ProfileEdit', 'ChangePassword', 'NotificationSettings', 'AutoDownloadSettings', 'PrivacySettings', 'SkipRewindSettings', 'SleepTimerSettings'].includes(pageName)) { pageName = 'MainTabbar'; }
      if (['Splash', 'MainTabbar', 'Login'].includes(pageName)) { return null; }
      if (!PAGES[pageName]) { return null; }
      let pageParams = { page: null, props: {} };
      pageParams.page = pageName;
      pageParams.routeName = route.name;
      pageParams.initFrom = route.meta.initFrom;
      if (route.params.id) { pageParams.props.id = parseInt(route.params.id); }
      if (route.params.slug) { pageParams.props.title = changeCase.titleCase(route.params.slug); }
      if (route.query.reset_password_token) { pageParams.props.resetPasswordToken = route.query.reset_password_token; }
      if (route.query.invitation_token) { pageParams.props.invitationToken = route.query.invitation_token; }
      return pageParams;
    },
    // initialze the navigator. Must be called before other methods. Called only once
    // This method properly sets up the back/forward button(s) of a device/browser. Without this method call back/forward button will cause undesirable behavior
    // It also sets up browser address bar sync. update the address to appropriate URL
    // In addition, it listens to deep link (opening an edifi url with the app) in order to handle it properly. Details - https://github.com/e-imaxina/cordova-plugin-deeplinks
    initializeNavigator() {
      if (this.$store.state.navigator.initialized) { return; }
      this.$store.commit('navigator/setInitialized');
      let that = this;
      // setup for back/forward buttons of device/browser
      this.$router.beforeEach((to, from, next) => { // the support for browser/hardware back/forward button is not perfect
        const toTs = to && to.query && to.query.ts ? parseInt(to.query.ts) : 0;
        const fromTs = from && from.query && from.query.ts ? parseInt(from.query.ts) : 0;
        const isPop = toTs < fromTs;
        if (!that.$store.state.navigator.isHardwareNavigation) {
          next();
          return;
        }
        if (isPop) {
          this.$store.commit('navigator/pop');
        } else {
          let pageObj = PAGES[changeCase.pascalCase(to.name)];
          let props = {};
          if (to.params.id) { props.id = parseInt(to.params.id); }
          if (to.params.slug) { props.title = changeCase.titleCase(to.params.slug); }
          this.$store.commit('navigator/push', {
            extends: pageObj,
            onsNavigatorProps: props,
          });
        }
        next();
      });
      // setup for notification clicks on web and PWA
      if (!this.$isCordova) {
        this.setupNotificationClickListener();
      }
      // setup for open urls with app on Cordova
      if (this.$isCordova) {
        this.setupDeepLinkListenerCordova();
      }
    },
    setupNotificationClickListener() {
      if (!!this.$isCordova) { return; }
      if (notificationClickListenerIsOn) { return; }
      notificationClickListenerIsOn = true;
      if (navigator.serviceWorker) {
        navigator.serviceWorker.addEventListener('message', (event) => {
          if (event.origin !== location.origin) { return; }
          if (!event.data || !event.data.tag) { return; }
          if (event.data.tag === 'new-ep-from-sub') {
            this.navigatorPush('Episode', { id: event.data.episode_id, title: event.data.body });
          }
        });
      }
    },
    setupNotificationClickListenerCordova(pushSubscription) {
      if (!this.$isCordova) { return; }
      if (notificationClickListenerIsOn) { return; }
      notificationClickListenerIsOn = true;
      let that = this;
      pushSubscription.on('notification', function (data) {
        if (data.additionalData.tag === 'new-ep-from-sub') {
          that.navigatorPush('Episode', { id: parseInt(data.additionalData.episode_id), title: data.message });
        }
      });
    },
    setupDeepLinkListenerCordova() {
      if (!this.$isCordova) { return; }
      let that = this;
      window.universalLinks.subscribe(null, function (eventData) {
        that.navigatorInitRoot(eventData.url);
      });
    },
    setIsHardwareNavigationFalse() {
      this.$store.commit('navigator/setIsHardwareNavigation', false);
    },
    setIsHardwareNavigationTrue() {
      setTimeout(() => {
        this.$store.commit('navigator/setIsHardwareNavigation', true);
      }, 200);
    },
    navigatorPop({ steps = 1 } = {}) {
      if (steps < 1) { steps = 1; }
      this.setIsHardwareNavigationFalse();
      for (var i = steps; i > 0; i--) {
        this.$router.back();
      }
      this.setIsHardwareNavigationTrue();
      this.$store.commit('navigator/pop', steps);
    },
    navigatorPush(page, props = {}, options = {}) {
      let pageObj = PAGES[page];
      if (pageObj.name) {
        let route = { name: pageObj.name, params: {}, query: { ts: Date.now() } };
        if (props.id) { route.params.id = props.id; }
        if (props.title) { route.params.slug = this.slugify(props.title); }
        this.setIsHardwareNavigationFalse();
        this.$router.push(route).catch((err) => {
          if (err.name === 'NavigationDuplicated') { return; }
          throw err;
        }).finally(() => { this.setIsHardwareNavigationTrue(); });
      }
      this.$store.commit('navigator/push', {
        extends: pageObj,
        onsNavigatorProps: props,
        onsNavigatorOptions: options,
      });
    },
    navigatorPushLogin({ allowPop = false } = {}) {
      this.navigatorPush('Login', { allowPop: allowPop }, { animation: 'lift' });
    },
    navigatorReplace(page, props = {}, options = {}) {
      let pageObj = PAGES[page];
      if (pageObj.name) {
        let ts = this.$route.query.ts || Date.now();
        let route = { name: pageObj.name, params: {}, query: { ts: ts } };
        if (props.id) { route.params.id = props.id; }
        if (props.title) { route.params.slug = this.slugify(props.title); }
        this.setIsHardwareNavigationFalse();
        this.$router.replace(route).catch((err) => {
          if (err.name === 'NavigationDuplicated') { return; }
          throw err;
        }).finally(() => { this.setIsHardwareNavigationTrue(); });
      }
      this.$store.commit('navigator/replace', {
        extends: PAGES[page],
        onsNavigatorProps: props,
        onsNavigatorOptions: options,
      });
    },
    navigatorReset(page, props = {}, options = {}) {
      this.setIsHardwareNavigationFalse();
      if (this.$store.state.navigator.stack.length >= 2) {
        this.$router.go(1 - this.$store.state.navigator.stack.length);
      }
      let pageObj = PAGES[page];
      this.$store.commit('navigator/reset', {
        extends: pageObj,
        onsNavigatorProps: props,
        onsNavigatorOptions: options,
      });
      if (page === 'MainTabbar') {
        this.navigatorSetMainTabbar(0);
      } else {
        let ts = this.$route.query.ts || Date.now();
        this.$router.replace({ name: pageObj.name, query: { ts: ts } }).finally(() => { this.setIsHardwareNavigationTrue(); });
      }
    },
    navigatorResetMain(props = {}, message) {
      this.navigatorReset('MainTabbar', props, { animation: 'lift' });
      if (message) {
        this.$ons.notification.toast(message, { timeout: 3000, animation: 'fall' });
      }
    },
    navigatorResetLogin(message) {
      this.navigatorReset('Login', {}, { animation: 'lift' });
      if (message) {
        this.$ons.notification.toast(message, { timeout: 3000, animation: 'fall' });
      }
    },
    navigatorOpenExternal(url) {
      window.open(url);
      // window.open(promotion.url, '_system', 'location=yes')navigatorOpenExternal
    },
    navigatorSetMainTabbar(newValue) {
      this.setIsHardwareNavigationFalse();
      if (this.$store.state.navigator.stack.length === 0) {
        this.$router.push({ name: this.$store.state.navigator.mainTabs[newValue].page.name, query: { ts: Date.now() } }).finally(() => { this.setIsHardwareNavigationTrue(); });
      } else {
        this.$router.replace({ name: this.$store.state.navigator.mainTabs[newValue].page.name, query: { ts: this.$route.query.ts } }).finally(() => { this.setIsHardwareNavigationTrue(); });
      }
      this.$store.commit('tabbar/set', newValue);
    },
    showCheckErrorsToast() {
      this.$ons.notification.toast(i18n.t("messages.check_errors"), { timeout: 3000, animation: 'fall' });
    },
    showSomethingWrongToast(additionalMsg) {
      let options = { timeout: 5000, animation: 'fall' };
      let message = i18n.t("messages.something_wrong") + (additionalMsg ? '\n'+additionalMsg : '');
      if (!this.isOffline) {
        options.buttonLabel = i18n.t("ui.go_offline");
        message = i18n.t("messages.check_internet") + (additionalMsg ? '\n'+additionalMsg : '');
      }
      this.$ons.notification.toast(message, options).then((res) => {
        if (res === 0) {
          this.goOffline();
        }
      });
    },
    showGoOnlineToast(msg) {
      if (!this.isOffline) { return; }
      return this.$ons.notification.toast(msg, { timeout: 3000, buttonLabel: this.$t("ui.go_online") }).then((res) => {
        if (res === 0) {
          this.goOnline();
        }
      });
    },
    checkAndShowRequireLoginToast() {
      if (this.currentUser && !this.currentUser._is_guest) { return true; }
      this.$ons.notification.toast(i18n.t("messages.login_required_for_action"), { timeout: 5000, buttonLabel: i18n.t("ui.login_now"), force: true, animation: 'fall' }).then((res) => {
        if (res === 0) {
          return true;
        } else {
          return false;
        }
      }).then((res) => {
        if (!res) { return false; }
        this.navigatorPushLogin({allowPop: true});
      });
      return false;
    },
    goOffline() {
      if (this.isOffline) { return ;}
      this.$store.commit('networkStatus/setOffline', true);
      this.navigatorInitRoot();
    },
    goOnline() {
      if (!this.isOffline) { return ;}
      this.$store.commit('networkStatus/setOffline', false);
      this.shouldShowGoOnlineToast = false;
      this.navigatorInitRoot();
    },
  },

  mounted() {
    this.shouldShowGoOnlineToast = this.isOffline;
  },
};
